<template>
  <ion-modal
    :is-open="true"
    :backdrop-dismiss="false"
    :keyboard-close="false"
  >
    <ion-content>
      <div class="flex flex-col flex-1 h-full">
        <ion-header>
          <ion-toolbar color="primary">
            <ion-title class="headline-lg text-white">Slutför registrering</ion-title>
          </ion-toolbar>
        </ion-header>
        <div
          class="flex-1 flex flex-col justify-between"
          :class="{
            'pb-540': Capacitor.isNativePlatform(),
          }"
        >
          <div v-if="step === 0" class="py-24 px-16">
            <div class="headline-medium mb-16">
              Mina uppgifter
            </div>
            <TextInput
              class="mb-16"
              placeholder="Förnamn"
              type="text"
              :invalid="validateFields.firstName"
              :error-text="ValidationStrings.Required"
              :value="firstName"
              @on-input="(input) => firstName = input"
            />
            <TextInput
              class="mb-16"
              placeholder="Efternamn"
              type="text"
              :value="lastName"
              :invalid="validateFields.lastName"
              :error-text="ValidationStrings.Required"
              @on-input="(input) => lastName = input"
            />
            <TextInput
              class="mb-16"
              placeholder="Telefonnummer"
              type="tel"
              :value="phone"
              sub-text="Rekommenderas"
              @on-input="(input) => phone = input"
            />
            <TextInput
              class="mb-16"
              placeholder="E-postadress"
              type="email"
              disabled
              :value="userStore.currentUser.email"
            />
            <CompanySelector
              @set-workplace="setWorkplace"
              @set-company="setCompany"
              @set-sector="setSector"
            />
            <div v-if="invalidCompany || invalidWorkplace" class="headline-small mt-12 text-error text-start">
              {{ ValidationStrings.Company }}
            </div>
          </div>
          <div
            class="mt-auto mx-16 mb-16"
          >
            <div v-if="loading" class="mb-12 headline-small text-primary-default flex justify-center">
              Vänligen vänta medan vi färdigställer din profil...
            </div>
            <Button
              text="Spara"
              type="primary"
              :disabled="!company || !company.org_nr || !workplace || !firstName || !lastName"
              :loading="loading"
              @click="saveUser"
            />
          </div>
        </div>
      </div>
    </ion-content>
  </ion-modal>
</template>
<script setup lang="ts">

import { IonContent, IonModal, IonHeader, IonToolbar, IonTitle } from '@ionic/vue';
import CompanySelector from '@/components/CompanySelector.vue';
import Button from '@/components/Button.vue';
import { useUserStore } from '@/store/user';
import { computed, ref } from 'vue';
import { Workplace } from '@/constants/types/api';
import ValidationStrings from '@/constants/validation-strings';
import TextInput from '@/components/form-elements/TextInput.vue';
import { Capacitor } from '@capacitor/core';

const userStore = useUserStore();

const company = ref({
  name: '',
  org_nr: '',
  sector: null as number | null,
  access_code: '',
});
const workplace = ref<Workplace | null>(null);
const firstName = ref<string>(userStore.currentUser.first_name);
const lastName = ref<string>(userStore.currentUser.last_name);
const phone = ref<string>('');

const attemptedSave = ref(false);
const step = ref(0);

const loading = ref(false);

const saveUser = async() => {
  loading.value = true;
  attemptedSave.value = true;
  if (invalidCompany.value || invalidWorkplace.value || !firstName.value || !lastName.value) {
    loading.value = false;
    return;
  }

  await userStore.updateUser({
    company: company.value,
    workplace: {
      name: workplace.value.Namn,
      address: workplace.value.Gatuadress,
      zip: workplace.value.Postnummer,
      city: workplace.value.Postort,
      id: workplace.value.ArbetsstalleID,
    },
    first_name: firstName.value,
    last_name: lastName.value,
    phone: phone.value,
  });
  loading.value = false;
};

const setWorkplace = (workplaceInput: Workplace) => {
  workplace.value = workplaceInput;
};

const setCompany = ({ name, orgNr }: { name: string; orgNr: string }) => {
  company.value = {
    ...company.value,
    name,
    org_nr: orgNr,
  };
};

const setSector = (sector: number) => {
  company.value = {
    ...company.value,
    sector,
  };
};

const validateFields = computed(() => {
  if (!attemptedSave.value) {
    return {
      firstName: false,
      lastName: false,
    };
  }
  return {
    firstName: !firstName.value,
    lastName: !lastName.value,
  };
});

const invalidCompany = computed(() => {
  return attemptedSave.value && (!company.value || !company.value.org_nr);
});

const invalidWorkplace = computed(() => {
  return attemptedSave.value && !workplace.value;
});
</script>
